import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import type {IssueFieldAddedEvent$key} from './__generated__/IssueFieldAddedEvent.graphql'
import type {IssueFieldChangedEvent$key} from './__generated__/IssueFieldChangedEvent.graphql'
import type {IssueFieldRemovedEvent$key} from './__generated__/IssueFieldRemovedEvent.graphql'
import {IssueFieldEvent, type IssueFieldEventBaseProps, normalizeMultiSelectOption} from './IssueFieldEvent'
import {RolledupIssueFieldEvent} from './RolledupIssueFieldEvent'

type IssueFieldEventKey = IssueFieldAddedEvent$key | IssueFieldChangedEvent$key | IssueFieldRemovedEvent$key

type IssueFieldChangedEventProps = IssueFieldEventBaseProps & {
  queryRef: IssueFieldChangedEvent$key & {createdAt?: string}
  rollupGroup?: Record<string, IssueFieldEventKey[]>
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueFieldChangedEventFragment = graphql`
  fragment IssueFieldChangedEvent on IssueFieldChangedEvent {
    databaseId
    actor {
      avatarUrl(size: 64)
      login
      profileResourcePath
      __typename
      ... on Bot {
        isCopilot
        isAgent
        displayName
      }
      ... on User {
        name
      }
      ... on EnterpriseUserAccount {
        name
      }
      ... on Mannequin {
        name
      }
    }
    createdAt
    issueField {
      ... on IssueFieldText {
        dataType
        name
      }
      ... on IssueFieldDate {
        name
        dataType
      }
      ... on IssueFieldNumber {
        name
        dataType
      }
      ... on IssueFieldSingleSelect {
        name
        dataType
      }
      ... on IssueFieldMultiSelect {
        name
        dataType
      }
    }
    newColor
    newOptions {
      name
      color
    }
    newValue
    intent {
      rationale
    }
  }
`

export function IssueFieldChangedEvent({queryRef, rollupGroup, ...props}: IssueFieldChangedEventProps) {
  const {actor, createdAt, issueField, newValue, newColor, newOptions, databaseId, intent} = useFragment(
    IssueFieldChangedEventFragment,
    queryRef,
  )
  const fieldOptions = newOptions?.map(option => normalizeMultiSelectOption(option.name, option.color))

  // If there's a rollup group, render the bundled view
  const rollupContent = rollupGroup ? (
    <RolledupIssueFieldEvent rollupGroup={rollupGroup} repositoryNameWithOwner={props.repositoryNameWithOwner} />
  ) : undefined

  return (
    <IssueFieldEvent
      actor={actor}
      createdAt={createdAt}
      fieldName={issueField?.name}
      dataType={issueField?.dataType}
      fieldValue={newValue}
      fieldOptions={fieldOptions}
      color={newColor}
      databaseId={databaseId}
      leadingLabel={rollupGroup ? undefined : LABELS.timeline.issueFieldChanged.leading}
      trailingLabel={rollupGroup ? undefined : LABELS.timeline.issueFieldChanged.trailing}
      hasValue={!rollupGroup}
      rollupContent={rollupContent}
      rationale={rollupGroup ? undefined : (intent?.rationale ?? undefined)}
      analyticsAction="ISSUE_FIELD_CHANGED"
      {...props}
    />
  )
}
